/**
 * The Modbus register map the Points step edits (RegisterBrowser): the rows a
 * read, a known map, an import or the operator listed, each retypable, and
 * which of them the operator ticked.
 *
 * A row's type is carried on the existing register shape, so the commit path
 * is unchanged: data_type int16 / int32 / float32 / double plus `unsigned`
 * (uint16 = int16 unsigned, uint32 = int32 unsigned), reg_count derived from
 * the type, and word_order / scale / unit alongside.
 *
 * A row retyped to a 2- or 4-register value covers the rows after it: those
 * are "consumed" (greyed, not selectable, dropped from the selection), and its
 * sampled value is re-decoded from the words already read (register-decode).
 *
 * Pure, so the whole edit flow is unit-tested without rendering.
 */

import type { ModbusWordOrder } from "@/lib/api/things";
import type { SniffedRegister } from "@/lib/gateway-client";
import type { PointRecord } from "./point-map-io";
import { validateModbusAddress } from "./address-validate";
import { registerKey } from "./points";
import { decodeRegisters, registersFor } from "./register-decode";
import { decodedText } from "./word-decode";

export type RegisterSpace = NonNullable<SniffedRegister["space"]>;

/** The types a Modbus row can be read as on the Points step. */
export type ModbusValueType = "bool" | "int16" | "uint16" | "int32" | "uint32" | "float32" | "float64";

/** The types of a holding or input register row. */
export const MODBUS_WORD_TYPES: ModbusValueType[] = ["int16", "uint16", "int32", "uint32", "float32", "float64"];

export const REGISTER_SPACES: RegisterSpace[] = ["holding", "input", "coil", "discrete"];

/** A coil or discrete input: a single bit. */
export function isBitSpace(space: string | null | undefined): boolean {
  return space === "coil" || space === "discrete";
}

/** The types a row in this space may have. */
export function modbusTypesFor(space: string | null | undefined): ModbusValueType[] {
  return isBitSpace(space) ? ["bool"] : MODBUS_WORD_TYPES;
}

/** Metric prefix per space, as a read names its rows ("hr_12"). */
const SPACE_PREFIX: Record<RegisterSpace, string> = { holding: "hr", input: "ir", coil: "coil", discrete: "di" };

export function defaultRegisterName(space: RegisterSpace, register: number): string {
  return `${SPACE_PREFIX[space]}_${register}`;
}

/** The type a row is read as. */
export function registerType(r: SniffedRegister): ModbusValueType {
  if (isBitSpace(r.space)) return "bool";
  switch (r.data_type) {
    case "double":
      return "float64";
    case "float32":
      return "float32";
    case "int32":
      return r.unsigned ? "uint32" : "int32";
    default:
      return r.unsigned ? "uint16" : "int16";
  }
}

/** How many registers a row's value spans. */
export function registerWords(r: SniffedRegister): number {
  return isBitSpace(r.space) ? 1 : registersFor(registerType(r));
}

/** A row read as `type`: its data_type / unsigned / reg_count, and ABCD when it now spans registers. */
export function withRegisterType(r: SniffedRegister, type: ModbusValueType): SniffedRegister {
  const data_type: SniffedRegister["data_type"] =
    type === "float64" ? "double" : type === "float32" ? "float32" : type === "int32" || type === "uint32" ? "int32" : "int16";
  const out: SniffedRegister = { ...r, data_type, reg_count: registersFor(type) };
  if (type === "uint16" || type === "uint32") out.unsigned = true;
  else delete out.unsigned;
  if (out.reg_count >= 2) out.word_order = r.word_order ?? "big";
  else delete out.word_order;
  return out;
}

/** Keep a read row's raw 16-bit word (a one-register integer read), so it can be re-decoded later. */
export function withRawWord(r: SniffedRegister): SniffedRegister {
  if (r.word !== undefined || isBitSpace(r.space) || r.reg_count !== 1 || r.data_type !== "int16") return r;
  const v = r.value;
  if (typeof v !== "number" || !Number.isInteger(v) || v < -32768 || v > 65535) return r;
  return { ...r, word: v & 0xffff };
}

/** The rows covered by an earlier row's 2- or 4-register value: covered row key -> the row that reads it. */
export function consumedRegisters(regs: readonly SniffedRegister[]): Map<string, SniffedRegister> {
  const byKey = new Map(regs.map((r) => [registerKey(r), r]));
  const ordered = [...regs]
    .filter((r) => !isBitSpace(r.space))
    .sort((a, b) => (a.space ?? "holding").localeCompare(b.space ?? "holding") || a.address - b.address);
  const out = new Map<string, SniffedRegister>();
  for (const r of ordered) {
    if (out.has(registerKey(r))) continue;
    const n = registerWords(r);
    for (let i = 1; i < n; i++) {
      const k = registerKey({ space: r.space, address: r.address + i });
      if (byKey.has(k)) out.set(k, r);
    }
  }
  return out;
}

export interface Sampled {
  /** What the Sampled column shows (scaled when the row has a scale). */
  text: string;
  /** The decoded value before the scale, when the row is scaled. */
  raw?: string;
}

/**
 * A row's sampled value as its type reads it: re-decoded from the raw words
 * of this row and the ones it covers when all were read, else what was read.
 */
export function sampledValue(regs: readonly SniffedRegister[], r: SniffedRegister): Sampled {
  const type = registerType(r);
  let value: number | string = r.value;
  if (type !== "bool" && r.word !== undefined) {
    const n = registersFor(type);
    const words: number[] = [r.word];
    for (let i = 1; i < n; i++) {
      const next = regs.find((x) => (x.space ?? "holding") === (r.space ?? "holding") && x.address === r.address + i);
      if (next?.word === undefined) break;
      words.push(next.word);
    }
    const decoded = decodeRegisters(words, type, r.word_order ?? "big");
    value = decoded ?? "—";
  }
  if (typeof value !== "number") return { text: value };
  const scaled = r.scale !== undefined && r.scale !== 1;
  return scaled ? { text: decodedText(value * r.scale!), raw: decodedText(value) } : { text: decodedText(value) };
}

/** The selection without rows that are consumed or no longer listed. */
export function selectableSelection(regs: readonly SniffedRegister[], selected: ReadonlySet<string>): Set<string> {
  const consumed = consumedRegisters(regs);
  const listed = new Set(regs.map(registerKey));
  return new Set([...selected].filter((k) => listed.has(k) && !consumed.has(k)));
}

/** The rows that go to the Map step (and into an export): ticked and not consumed, in list order. */
export function mappedRegisters(regs: readonly SniffedRegister[], selected: ReadonlySet<string>): SniffedRegister[] {
  const keep = selectableSelection(regs, selected);
  return regs.filter((r) => keep.has(registerKey(r)));
}

export interface RegisterMapState {
  registers: SniffedRegister[];
  selected: Set<string>;
  /** Why the edit was refused (the state is unchanged). */
  error?: string;
}

/** One row's editable fields; `scale: null` clears the scale. */
export interface RegisterEdit {
  name?: string;
  type?: ModbusValueType;
  unit?: string;
  scale?: number | null;
  word_order?: ModbusWordOrder;
}

/**
 * Edit the row at `key` (its register space and register stay: add a row for
 * another address). A coil / discrete row stays bool. The selection loses
 * whatever the edit now consumes.
 */
export function editRegister(
  regs: readonly SniffedRegister[],
  selected: ReadonlySet<string>,
  key: string,
  edit: RegisterEdit,
): RegisterMapState {
  const i = regs.findIndex((r) => registerKey(r) === key);
  if (i < 0) return { registers: [...regs], selected: new Set(selected), error: "That row is no longer listed." };
  let r: SniffedRegister = { ...regs[i] };
  const sel = new Set(selected);
  if (edit.type) r = withRegisterType(r, isBitSpace(r.space) ? "bool" : edit.type === "bool" ? "int16" : edit.type);
  if (edit.name !== undefined) r.guessed_name = edit.name;
  if (edit.unit !== undefined) r.unit = edit.unit || undefined;
  if (edit.scale !== undefined) {
    if (edit.scale === null || edit.scale === 1) delete r.scale;
    else r.scale = edit.scale;
  }
  if (edit.word_order !== undefined && registerWords(r) >= 2) r.word_order = edit.word_order;
  const next = [...regs];
  next[i] = r;
  return { registers: next, selected: selectableSelection(next, sel) };
}

/** Drop a row from the list (and the selection). */
export function removeRegister(
  regs: readonly SniffedRegister[],
  selected: ReadonlySet<string>,
  key: string,
): RegisterMapState {
  const next = regs.filter((r) => registerKey(r) !== key);
  return { registers: next, selected: selectableSelection(next, selected) };
}

export interface NewRegister {
  space: RegisterSpace;
  /** The 0-based register, as typed. */
  register: string | number;
  type: ModbusValueType;
  /** Blank => the read's naming ("hr_12"). */
  name?: string;
  unit?: string;
  scale?: number;
  word_order?: ModbusWordOrder;
}

/**
 * Add a row by hand, ticked. Refused (with the platform's reason) when the
 * register is not a Modbus address, it is already listed, or it is covered by
 * a listed wider value.
 */
export function addRegister(
  regs: readonly SniffedRegister[],
  selected: ReadonlySet<string>,
  input: NewRegister,
): RegisterMapState {
  const same = { registers: [...regs], selected: new Set(selected) };
  const text = String(input.register).trim();
  const register = /^-?\d+$/.test(text) ? Number(text) : text === "" ? undefined : Number.NaN;
  const bad = validateModbusAddress({ space: input.space, register: Number.isNaN(register) ? text : register });
  if (bad) return { ...same, error: bad.replace(/^modbus address: /, "") };
  const reg = register as number;
  const key = registerKey({ space: input.space, address: reg });
  if (regs.some((r) => registerKey(r) === key)) return { ...same, error: `${input.space} ${reg} is already listed.` };
  const name = input.name?.trim() || defaultRegisterName(input.space, reg);
  if (regs.some((r) => r.guessed_name === name)) return { ...same, error: `Another row is already named ${name}.` };
  let row: SniffedRegister = {
    address: reg,
    data_type: "int16",
    reg_count: 1,
    value: "—",
    guessed_name: name,
    ...(input.space !== "holding" ? { space: input.space } : {}),
    ...(input.unit?.trim() ? { unit: input.unit.trim() } : {}),
    ...(input.scale !== undefined && input.scale !== 1 ? { scale: input.scale } : {}),
  };
  row = withRegisterType(row, isBitSpace(input.space) ? "bool" : input.type);
  if (input.word_order && row.reg_count >= 2) row.word_order = input.word_order;
  const next = [...regs, row];
  const covered = consumedRegisters(next).get(key);
  if (covered) {
    return { ...same, error: `${input.space} ${reg} is read with ${covered.guessed_name} (${registerType(covered)}).` };
  }
  return { registers: next, selected: selectableSelection(next, new Set([...selected, key])) };
}

/**
 * Name problems among the rows that will be mapped: an invalid metric, or a
 * name used twice (the create refuses a duplicate). Row key -> the problem.
 */
export function registerNameProblems(regs: readonly SniffedRegister[], selected: ReadonlySet<string>): Map<string, string> {
  const out = new Map<string, string>();
  const first = new Map<string, SniffedRegister>();
  for (const r of mappedRegisters(regs, selected)) {
    const name = r.guessed_name.trim();
    const k = registerKey(r);
    if (!name) out.set(k, "Name this register.");
    else if (!/^[A-Za-z0-9_.-]+$/.test(name)) out.set(k, "Letters, digits, _ . or - only.");
    else if (first.has(name)) out.set(k, `Same name as ${first.get(name)!.space ?? "holding"} ${first.get(name)!.address}.`);
    else first.set(name, r);
  }
  return out;
}

// ── map records ─────────────────────────────────────────────────────────────

/** A row as a map record (driver-neutral). */
export function registerToRecord(r: SniffedRegister): PointRecord {
  const address: Record<string, unknown> = { space: r.space ?? "holding", register: r.address };
  if (registerWords(r) >= 2) address.word_order = r.word_order ?? "big";
  return {
    name: r.guessed_name,
    type: registerType(r),
    address,
    ...(r.unit ? { unit: r.unit } : {}),
    ...(r.scale !== undefined && r.scale !== 1 ? { scale: r.scale } : {}),
    ...(r.poll_sec !== undefined ? { poll_sec: r.poll_sec } : {}),
    ...(r.deadband !== undefined ? { deadband: r.deadband } : {}),
    ...(r.max_silence_sec !== undefined ? { max_silence_sec: r.max_silence_sec } : {}),
  };
}

/** A (validated) Modbus map record as a row, with nothing read yet. */
export function recordToRegister(rec: PointRecord): SniffedRegister {
  const space = (typeof rec.address.space === "string" ? rec.address.space : "holding") as RegisterSpace;
  const base: SniffedRegister = {
    address: Number(rec.address.register),
    data_type: "int16",
    reg_count: 1,
    value: "—",
    guessed_name: rec.name,
    ...(space !== "holding" ? { space } : {}),
    ...(rec.unit ? { unit: rec.unit } : {}),
    ...(rec.scale !== undefined && rec.scale !== 1 ? { scale: rec.scale } : {}),
    ...(rec.poll_sec !== undefined ? { poll_sec: rec.poll_sec } : {}),
    ...(rec.deadband !== undefined ? { deadband: rec.deadband } : {}),
    ...(rec.max_silence_sec !== undefined ? { max_silence_sec: rec.max_silence_sec } : {}),
  };
  const type = (isBitSpace(space) ? "bool" : rec.type) as ModbusValueType;
  const row = withRegisterType(base, MODBUS_WORD_TYPES.includes(type) || type === "bool" ? type : "int16");
  const wo = rec.address.word_order;
  if (row.reg_count >= 2 && typeof wo === "string") row.word_order = wo as ModbusWordOrder;
  return row;
}

/**
 * Apply imported records: replace the list, or append, where a record for a
 * register already listed retypes and renames that row (keeping what was read,
 * so it re-decodes) instead of adding a second one. Every imported row is ticked.
 */
export function mergeRegisterRecords(
  regs: readonly SniffedRegister[],
  selected: ReadonlySet<string>,
  records: readonly PointRecord[],
  mode: "append" | "replace",
): RegisterMapState & { added: number; updated: number } {
  if (mode === "replace") {
    const next = records.map(recordToRegister);
    return { registers: next, selected: selectableSelection(next, new Set(next.map(registerKey))), added: next.length, updated: 0 };
  }
  const next = [...regs];
  const sel = new Set(selected);
  let added = 0;
  let updated = 0;
  for (const rec of records) {
    const fresh = recordToRegister(rec);
    const k = registerKey(fresh);
    const i = next.findIndex((r) => registerKey(r) === k);
    if (i >= 0) {
      const keep = next[i];
      next[i] = { ...fresh, value: keep.value, ...(keep.word !== undefined ? { word: keep.word } : {}) };
      updated++;
    } else {
      next.push(fresh);
      added++;
    }
    sel.add(k);
  }
  return { registers: next, selected: selectableSelection(next, sel), added, updated };
}
